/**
 * External Tasks API Service - Infinite scroll wrapper
 *
 * This service wraps the generated external tasks API with infinite query support.
 * The backend provides a standard paginated list endpoint, and we wrap it with
 * RTK Query's infiniteQuery for smooth infinite scrolling on the frontend.
 *
 * Pattern follows policeDepartmentsService.ts approach.
 *
 * Also enhances the generated API with proper cache invalidation for task links
 * and polling support for pending actions.
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { baseQueryWithErrorHandling } from './baseQuery';
import {
  enhancedApi as generatedApi,
  type ExternalTaskActionSchema,
  type ExternalTaskDetailSchema,
  type ExternalTaskListApiArg,
  type ExternalTaskListItemSchema,
} from './generated/externalTaskService';

// Re-export the generated type for convenience
export type ExternalTasksFilter = Omit<
  ExternalTaskListApiArg,
  'limit' | 'offset'
>;

export interface ExternalTasksSearchResults {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: ExternalTaskListItemSchema[];
}

/**
 * Check if an external task has any pending or in-progress actions
 * that should trigger polling for status updates.
 */
export const hasPendingActions = (
  task: ExternalTaskDetailSchema | undefined,
): boolean => {
  if (!task?.pending_actions) return false;
  return task.pending_actions.some(
    (action) => action.status === 'PENDING' || action.status === 'IN_PROGRESS',
  );
};

/**
 * Get the polling interval based on action status.
 * Returns 0 (no polling) if no actions are pending.
 */
export const getPollingInterval = (
  task: ExternalTaskDetailSchema | undefined,
): number => {
  if (!hasPendingActions(task)) return 0;
  // Poll every 2 seconds while actions are in progress
  return 2000;
};

export const externalTasksApiClient = createApi({
  reducerPath: 'externalTasksApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['ExternalTasks'],
  endpoints: (builder) => ({
    getExternalTasksInfinite: builder.infiniteQuery<
      ExternalTasksSearchResults,
      ExternalTasksFilter,
      {
        offset: number;
        limit: number;
      }
    >({
      infiniteQueryOptions: {
        initialPageParam: {
          offset: 0,
          limit: 100,
        },
        getNextPageParam: (lastPage, pages, lastPageParam) => {
          const totalFetched = lastPageParam.offset + lastPageParam.limit;
          if (totalFetched >= lastPage.count) return undefined;
          return {
            offset: totalFetched,
            limit: lastPageParam.limit,
          };
        },
        getPreviousPageParam: (firstPage, allPages, firstPageParam) => {
          if (firstPageParam.offset === 0) return undefined;
          return {
            offset: Math.max(0, firstPageParam.offset - firstPageParam.limit),
            limit: firstPageParam.limit,
          };
        },
      },
      query: ({ queryArg, pageParam }) => {
        const params = new URLSearchParams({
          limit: pageParam.limit.toString(),
          offset: pageParam.offset.toString(),
        });

        // Multi-value filters (arrays)
        if (queryArg.search) {
          queryArg.search.forEach((term) => {
            params.append('search', term);
          });
        }
        if (queryArg.assigneeName) {
          queryArg.assigneeName.forEach((name) => {
            params.append('assignee_name', name);
          });
        }
        if (queryArg.status) {
          queryArg.status.forEach((status) => {
            params.append('status', status);
          });
        }
        if (queryArg.source) {
          queryArg.source.forEach((source) => {
            params.append('source', source);
          });
        }
        if (queryArg.firm) {
          queryArg.firm.forEach((firm) => {
            params.append('firm', firm);
          });
        }
        if (queryArg.matter) {
          queryArg.matter.forEach((matter) => {
            params.append('matter', matter);
          });
        }

        // Single-value filters
        if (queryArg.ordering) params.set('ordering', queryArg.ordering);
        if (queryArg.needsUpdate !== undefined)
          params.set('needs_update', queryArg.needsUpdate.toString());
        if (queryArg.linkedTasksMin !== undefined)
          params.set('linked_tasks_min', queryArg.linkedTasksMin.toString());
        if (queryArg.linkedTasksMax !== undefined)
          params.set('linked_tasks_max', queryArg.linkedTasksMax.toString());

        // Date filters
        if (queryArg.createdAtAfter)
          params.set('created_at_after', queryArg.createdAtAfter);
        if (queryArg.createdAtBefore)
          params.set('created_at_before', queryArg.createdAtBefore);
        if (queryArg.dueDateAfter)
          params.set('due_date_after', queryArg.dueDateAfter);
        if (queryArg.dueDateBefore)
          params.set('due_date_before', queryArg.dueDateBefore);
        if (queryArg.lastSyncedAtAfter)
          params.set('last_synced_at_after', queryArg.lastSyncedAtAfter);
        if (queryArg.lastSyncedAtBefore)
          params.set('last_synced_at_before', queryArg.lastSyncedAtBefore);
        if (queryArg.lastActivityAfter)
          params.set('last_activity_after', queryArg.lastActivityAfter);
        if (queryArg.lastActivityBefore)
          params.set('last_activity_before', queryArg.lastActivityBefore);

        // Rule matching filters
        if (queryArg.hasMatchedRule !== undefined)
          params.set('has_matched_rule', queryArg.hasMatchedRule.toString());
        if (queryArg.isExcluded !== undefined)
          params.set('is_excluded', queryArg.isExcluded.toString());
        if (queryArg.slaStatus) {
          queryArg.slaStatus.forEach((status) => {
            params.append('sla_status', status);
          });
        }

        return {
          url: `/api/external-tasks/?${params.toString()}`,
        };
      },
      providesTags: (result, error, filter) => [
        {
          type: 'ExternalTasks',
          id: JSON.stringify({ ...filter, offset: undefined }),
        },
        { type: 'ExternalTasks' },
      ],
    }),
  }),
});

export const { useGetExternalTasksInfiniteInfiniteQuery } =
  externalTasksApiClient;

/**
 * Enhanced version of the generated API with proper cache invalidation.
 *
 * This ensures that when task actions are performed (status change, comment, due date,
 * or link operations), both the detail view and list view are properly invalidated
 * and refetch to show updated data.
 *
 * Action mutations now return ExternalTaskActionSchema with status tracking.
 * The UI should show optimistic updates and poll while actions are PENDING/IN_PROGRESS.
 */
export const enhancedApi = generatedApi.enhanceEndpoints({
  addTagTypes: ['external-task', 'external-task-link', 'ExternalTasks'],
  endpoints: {
    externalTaskRetrieve: {
      providesTags: (result, error, { id }) => [
        { type: 'external-task', id },
        'external-task',
      ],
    },
    externalTaskLinkList: {
      providesTags: (result, error, { externalTaskPk }) => [
        { type: 'external-task-link', id: externalTaskPk },
        'external-task-link',
      ],
    },
    externalTaskLinkCreate: {
      invalidatesTags: (result, error, { externalTaskPk }) => [
        { type: 'external-task-link', id: externalTaskPk },
        { type: 'external-task', id: externalTaskPk },
        'external-tasks', // Refresh list to show updated linked_tasks_count
      ],
    },
    externalTaskLinkDestroy: {
      invalidatesTags: (result, error, { externalTaskPk }) => [
        { type: 'external-task-link', id: externalTaskPk },
        { type: 'external-task', id: externalTaskPk },
        'external-tasks', // Refresh list to show updated linked_tasks_count
        'ExternalTasks', // Refresh infinite query
      ],
    },
    // Action mutations - these return immediately with action schema
    // and DON'T invalidate until action completes (via polling)
    externalTaskAddComment: {
      // Don't invalidate immediately - let polling handle the refresh
      // when the action completes
      invalidatesTags: () => [],
      // Optimistic update: add the pending action to the task's pending_actions
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: action } = await queryFulfilled;
          // Update the cached task detail to include the new pending action
          dispatch(
            generatedApi.util.updateQueryData(
              'externalTaskRetrieve',
              { id },
              (draft) => {
                if (draft.pending_actions) {
                  draft.pending_actions.push(action);
                } else {
                  draft.pending_actions = [action];
                }
              },
            ),
          );
        } catch {
          // Error handled by RTK Query
        }
      },
    },
    externalTaskChangeStatus: {
      invalidatesTags: () => [],
      async onQueryStarted(
        { id, changeStatusPayload },
        { dispatch, queryFulfilled },
      ) {
        // Optimistic update: immediately show the new status
        const patchResult = dispatch(
          generatedApi.util.updateQueryData(
            'externalTaskRetrieve',
            { id },
            (draft) => {
              // Optimistically update the status
              draft.status = changeStatusPayload.status;
            },
          ),
        );
        try {
          const { data: action } = await queryFulfilled;
          // Add the action to pending_actions
          dispatch(
            generatedApi.util.updateQueryData(
              'externalTaskRetrieve',
              { id },
              (draft) => {
                if (draft.pending_actions) {
                  draft.pending_actions.push(action);
                } else {
                  draft.pending_actions = [action];
                }
              },
            ),
          );
        } catch {
          // Revert optimistic update on error
          patchResult.undo();
        }
      },
    },
    externalTaskChangeDueDate: {
      invalidatesTags: () => [],
      async onQueryStarted(
        { id, changeDueDatePayload },
        { dispatch, queryFulfilled },
      ) {
        // Optimistic update: immediately show the new due date
        const patchResult = dispatch(
          generatedApi.util.updateQueryData(
            'externalTaskRetrieve',
            { id },
            (draft) => {
              draft.due_date = changeDueDatePayload.due_date;
            },
          ),
        );
        try {
          const { data: action } = await queryFulfilled;
          // Add the action to pending_actions
          dispatch(
            generatedApi.util.updateQueryData(
              'externalTaskRetrieve',
              { id },
              (draft) => {
                if (draft.pending_actions) {
                  draft.pending_actions.push(action);
                } else {
                  draft.pending_actions = [action];
                }
              },
            ),
          );
        } catch {
          // Revert optimistic update on error
          patchResult.undo();
        }
      },
    },
    externalTaskRetryAction: {
      invalidatesTags: () => [],
      async onQueryStarted({ id, actionId }, { dispatch, queryFulfilled }) {
        try {
          const { data: action } = await queryFulfilled;
          // Update the action in pending_actions
          dispatch(
            generatedApi.util.updateQueryData(
              'externalTaskRetrieve',
              { id },
              (draft) => {
                if (draft.pending_actions) {
                  const index = draft.pending_actions.findIndex(
                    (a) => a.id === actionId,
                  );
                  if (index !== -1) {
                    draft.pending_actions[index] = action;
                  } else {
                    draft.pending_actions.push(action);
                  }
                } else {
                  draft.pending_actions = [action];
                }
              },
            ),
          );
        } catch {
          // Error handled by RTK Query
        }
      },
    },
    externalTaskDismissAction: {
      invalidatesTags: () => [],
      async onQueryStarted({ id, actionId }, { dispatch, queryFulfilled }) {
        // Optimistic update: immediately remove the action from pending_actions
        const patchResult = dispatch(
          generatedApi.util.updateQueryData(
            'externalTaskRetrieve',
            { id },
            (draft) => {
              if (draft.pending_actions) {
                draft.pending_actions = draft.pending_actions.filter(
                  (a) => a.id !== actionId,
                );
              }
            },
          ),
        );
        try {
          await queryFulfilled;
          // Success - the optimistic update stays
        } catch {
          // Revert optimistic update on error
          patchResult.undo();
        }
      },
    },
    externalTaskBulkLink: {
      invalidatesTags: () => [
        'external-task', // Invalidate all task details
        'external-task-link', // Invalidate all links
        'external-tasks', // Refresh list
        'ExternalTasks', // Refresh infinite query
      ],
    },
  },
});

// Re-export types for convenience
export type { ExternalTaskActionSchema, ExternalTaskDetailSchema };
